import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSequenceAnnotations, setAnnotationSetForUpdate, clearDirtyFlagOnFinding } from '../annotation.slice';
import { AnnotationsForGrid, NewFindingStatus } from '../annotation.types';
import axios from '../../../../axios';
import { AppThunk } from '../../../../store';
import { AnnotationStandardDetails, FindingTypeProperty } from '../../AnnotationStandardForm/AnnotationStandardForm.slice';
import { localizationPropertyAssignment, AssessmentAssigneeOption } from './Helpers';
import { InspectionType } from '../../../../shared/models/InspectionType';
import { ToastAnt } from '../../../Shared/Toast/ToastAnt';
import { setSaveFinding } from '../../Canvas/canvas.slice';
import { isStringNullOrEmpty } from './Utils';
import { FailureMechanismTemplate } from '../../FailureMechanismTemplate/failureMechanismTemplate.slice';
import { LOCALIZATION_FIELDS } from '../../AnnotationStandardForm/AddFindingTypes/SelectedFindingType';
import {
  AnnotationAssessment,
  AnnotationAssessmentInfo,
  CreateAnnotation,
  CreateAnnotationProperty,
  RapairTriggerRule,
  RepairStatus,
  SendForAssessmentDto,
  SetQAReviewPassedDto,
  UpdateAnnotation,
  UpdateAnnotationShape,
  UpdateMultiAnnotations
} from './AnnotationForm.types';
import { getAnnotationComments } from './Comments/AnnotationComments.slice';

type State = {
  showCreateAnnotationModal: boolean;
  showMultiAnnotationModal: boolean;
  showDeleteAnnotationDialog: boolean;
  selectedAnnotationForEdit: AnnotationsForGrid | null;
  multipleSelectedAnnotationsForEdit: AnnotationsForGrid[] | null;
  selectedAnnotationIdsForDelete: string[] | null;
  selectedFindingTypeId: string;
  selectedProperties: Array<FindingTypeProperty>;
  initialDistanceToRoot: string;
  assessment: AnnotationAssessment | null;
  loadingAssessment: boolean;
  isSgreUser: boolean;
  isWarrantyCaseClosed: boolean;
  canSaveAssessment: boolean;
  submittingForm: boolean;
  goFromFindingToImage: boolean;
  repairTriggerRules: RapairTriggerRule[];
  loadingRepairTriggers: boolean;
  selectedFailureMechanism: FailureMechanismTemplate | null;
  repairStatus: RepairStatus | null;
};

const initialState: State = {
  showCreateAnnotationModal: false,
  showMultiAnnotationModal: false,
  showDeleteAnnotationDialog: false,
  selectedAnnotationForEdit: null,
  multipleSelectedAnnotationsForEdit: null,
  selectedAnnotationIdsForDelete: null,
  selectedProperties: [],
  selectedFindingTypeId: '',
  initialDistanceToRoot: '',
  assessment: null,
  loadingAssessment: false,
  isSgreUser: false,
  canSaveAssessment: false,
  submittingForm: false,
  goFromFindingToImage: false,
  isWarrantyCaseClosed: true,
  repairTriggerRules: [],
  loadingRepairTriggers: false,
  selectedFailureMechanism: null,
  repairStatus: null
};

const annotationFormSlice = createSlice({
  name: 'annotationForm',
  initialState,
  reducers: {
    resetState(state) {
      return initialState;
    },
    setShowCreateAnnotationModal(state, action: PayloadAction<boolean>) {
      state.showCreateAnnotationModal = action.payload;

      if (!action.payload) {
        state.selectedAnnotationForEdit = null;
      }

      state.selectedProperties = [];
      state.selectedFindingTypeId = '';
    },
    setShowMultiAnnotationModal(state, action: PayloadAction<boolean>) {
      state.showMultiAnnotationModal = action.payload;

      if (!action.payload) {
        state.selectedAnnotationForEdit = null;
      }

      state.selectedProperties = [];
      state.selectedFindingTypeId = '';
    },
    setShowDeleteAnnotationDialog(state, action: PayloadAction<boolean>) {
      state.showDeleteAnnotationDialog = action.payload;
    },
    setAnnotationIdsForDelete(state, action: PayloadAction<string[] | null>) {
      state.selectedAnnotationIdsForDelete = action.payload;
    },
    setSelectedAnnotationForEdit(state, action: PayloadAction<AnnotationsForGrid | null>) {
      state.selectedAnnotationForEdit = action.payload;
    },
    setMultipleSelectedAnnotationsForEdit(state, action: PayloadAction<AnnotationsForGrid[] | null>) {
      state.multipleSelectedAnnotationsForEdit = action.payload;
      state.showMultiAnnotationModal = true;
    },
    setSelectedProperties(state, action: PayloadAction<Array<FindingTypeProperty>>) {
      state.selectedProperties = action.payload;
    },
    setSelectedFindingTypeId(state, action: PayloadAction<string>) {
      state.selectedFindingTypeId = action.payload;
    },
    setDistanceToRoot(state, action: PayloadAction<string>) {
      state.initialDistanceToRoot = action.payload;
    },

    setAssessmentInfo(state, action: PayloadAction<AnnotationAssessmentInfo>) {
      const { isSgreUser, assessment } = action.payload;
      state.assessment = assessment;
      state.isSgreUser = isSgreUser;
    },
    setWarrantyCaseClosed(state, action: PayloadAction<boolean>) {
      state.isWarrantyCaseClosed = action.payload;
    },
    setLoadingAssessment(state, action: PayloadAction<boolean>) {
      state.loadingAssessment = action.payload;
    },
    completeAnnotationAssessment(state) {
      state.assessment!.status = NewFindingStatus.AssessmentCompleted;
    },
    setCanSaveAssessment(state, action: PayloadAction<boolean>) {
      state.canSaveAssessment = action.payload;
    },
    setSubmittingForm(state, action: PayloadAction<boolean>) {
      state.submittingForm = action.payload;
    },
    setGoFromFindingToImage(state, action: PayloadAction<boolean>) {
      state.goFromFindingToImage = action.payload;
    },
    setRepairTriggerRules(state, action: PayloadAction<RapairTriggerRule[]>) {
      state.repairTriggerRules = action.payload;
    },
    setLoadingRepairTriggers(state, action: PayloadAction<boolean>) {
      state.loadingRepairTriggers = action.payload;
    },
    setSelectedFailureMechanism(state, action: PayloadAction<FailureMechanismTemplate>) {
      state.selectedFailureMechanism = action.payload;
    },
    setRepairStatus(state, action: PayloadAction<RepairStatus | null>) {
      state.repairStatus = action.payload;
    }
  }
});

export const {
  resetState,
  setSelectedAnnotationForEdit,
  setShowCreateAnnotationModal,
  setShowMultiAnnotationModal,
  setMultipleSelectedAnnotationsForEdit,
  setSelectedFindingTypeId,
  setSelectedProperties,
  setShowDeleteAnnotationDialog,
  setAnnotationIdsForDelete,
  setDistanceToRoot,
  setAssessmentInfo,
  setWarrantyCaseClosed,
  setLoadingAssessment,
  completeAnnotationAssessment,
  setCanSaveAssessment,
  setSubmittingForm,
  setGoFromFindingToImage,
  setRepairTriggerRules,
  setLoadingRepairTriggers,
  setSelectedFailureMechanism,
  setRepairStatus
} = annotationFormSlice.actions;

export default annotationFormSlice.reducer;

//TODO: validations are probably not needed at all, clean it up
export const setInitialDistanceToRoot = (): AppThunk => async (dispatch, getState) => {
  const sequenceInfo = getState().annotation.selectedSequenceInfo;
  const selectedImage = getState().annotation.selectedName;
  if (!sequenceInfo) {
    return;
  }
  const inspectionType = sequenceInfo.inspectionType;
  if (inspectionType !== InspectionType.RopeAccess) {
    return;
  }
  const images = sequenceInfo.images;
  if (!(images && images.length > 0)) {
    return;
  }
  const image = images.find(x => x.filename === selectedImage);
  if (!image) {
    return;
  }
  if (image.distanceToRoot) {
    const distanceToRoot: string = image.distanceToRoot.toString();
    dispatch(setDistanceToRoot(distanceToRoot));
  }
};

export const updateMultiAnnotations =
  (name: string, message: string, properties: CreateAnnotationProperty[]): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setSubmittingForm(true));

      const findingTypeId = getState().annotationForm.selectedFindingTypeId;
      const ids = getState().annotationForm.multipleSelectedAnnotationsForEdit?.map(x => x.id);
      const imageFileName = getState().annotationForm.multipleSelectedAnnotationsForEdit![0].imageName;
      const sequencePathId = getState().annotationForm.multipleSelectedAnnotationsForEdit![0].sequencePath;

      if (!ids) {
        ToastAnt('An error occurred, nothing selected...');
        return;
      }

      const updateMultiAnnotations: UpdateMultiAnnotations = {
        ids,
        sequencePathId,
        imageFileName,
        findingTypeId: findingTypeId ? findingTypeId : getState().annotationForm.multipleSelectedAnnotationsForEdit![0].findingTypeId,
        message,
        properties,
        findingTypeName: name
      };
      const url = `annotations/multi-update`;
      await axios.put<AnnotationsForGrid[]>(url, updateMultiAnnotations);

      dispatch(getSequenceAnnotations(true, true));
      dispatch(setShowMultiAnnotationModal(false));
      ToastAnt('Updated');
    } catch (error) {
      ToastAnt('An error occured.');
    }
    dispatch(setSubmittingForm(false));
  };

export const createNewAnnotation =
  (name: string, properties: CreateAnnotationProperty[]): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setSubmittingForm(true));

      const annotationStandardId = getState().annotation.annotationStandardDetails!.id!;
      const annotationStandardName = getState().annotation.annotationStandardDetails!.name;
      const imageName = getState().annotation.selectedName;
      const findingTypeId = getState().annotationForm.selectedFindingTypeId;
      const sequencePath = getState().annotation.sequencePath;
      const shape = getState().canvas.activeShape;
      const imageScale = getState().canvas.imageScale;
      const createAnnotation: CreateAnnotation = {
        annotationStandardId,
        annotationStandardName,
        findingTypeId,
        findingTypeName: name,
        imageFileName: imageName,
        shape,
        imageScale,
        sequencePathId: sequencePath,
        properties
      };
      const url = `annotations`;
      const response = await axios.post<AnnotationsForGrid>(url, createAnnotation);

      dispatch(getSequenceAnnotations(true, true));
      dispatch(setSelectedAnnotationForEdit(response.data));
      dispatch(setShowCreateAnnotationModal(false));
      dispatch(setSubmittingForm(false));
      ToastAnt('Added');
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export const updateAnnotation =
  (name: string, properties: CreateAnnotationProperty[]): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setSubmittingForm(true));

      const findingTypeId = getState().annotationForm.selectedFindingTypeId;
      const annotationId = getState().annotationForm.selectedAnnotationForEdit!.id;
      const shape = getState().annotationForm.selectedAnnotationForEdit!.shape;
      const imageScale = getState().annotationForm.selectedAnnotationForEdit!.imageScale;
      const imageName = getState().annotationForm.selectedAnnotationForEdit!.imageName;
      const sequencePath = getState().annotationForm.selectedAnnotationForEdit!.sequencePath;
      const updatedAnnotation: UpdateAnnotation = {
        sequencePathId: sequencePath,
        imageFileName: imageName,
        id: annotationId,
        findingTypeId: findingTypeId ? findingTypeId : getState().annotationForm.selectedAnnotationForEdit!.findingTypeId,
        properties,
        shape,
        imageScale,
        findingTypeName: name
      };

      const url = `annotations`;
      const response = await axios.put<AnnotationsForGrid>(url, updatedAnnotation);

      dispatch(getSequenceAnnotations(true, true));
      dispatch(setCanSaveAssessment(true));
      dispatch(setSubmittingForm(false));
      ToastAnt('Updated');
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export const setQAReviewPassed = (): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(setSubmittingForm(true));

    const annotationId = getState().annotationForm.selectedAnnotationForEdit!.id;
    const imageName = getState().annotationForm.selectedAnnotationForEdit!.imageName;
    const sequencePath = getState().annotationForm.selectedAnnotationForEdit!.sequencePath;

    const setQAReviewPassedDto: SetQAReviewPassedDto = {
      sequencePathId: sequencePath,
      imageFileName: imageName,
      id: annotationId
    };

    dispatch(callSetQAReviewPassedAPI(setQAReviewPassedDto));

    dispatch(getSequenceAnnotations(true, true));
    dispatch(setSubmittingForm(false));
    dispatch(setShowCreateAnnotationModal(false));
    ToastAnt('Status set to QA Review Passed');
  } catch (error) {
    ToastAnt('An error occured.');
  }
};

export const setQAReviewPassedFromGrid =
  (setQAReviewPassedDto: SetQAReviewPassedDto): AppThunk =>
  async dispatch => {
    try {
      dispatch(callSetQAReviewPassedAPI(setQAReviewPassedDto));

      dispatch(getSequenceAnnotations(true, true));
      ToastAnt('Status set to QA Review Passed');
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export const callSetQAReviewPassedAPI =
  (setQAReviewPassedDto: SetQAReviewPassedDto): AppThunk =>
  async () => {
    try {
      const url = `annotations/set-qa-review-passed`;
      await axios.post<AnnotationsForGrid>(url, setQAReviewPassedDto);
    } catch (error) {
      ToastAnt('An error occured in set QA review passed API call.');
    }
  };

function getFindingTypePropNames(annotationStandardDetails: AnnotationStandardDetails, selectedAnnotationForEdit: AnnotationsForGrid) {
  return (
    annotationStandardDetails?.findingTypes
      .find(x => x.id === selectedAnnotationForEdit?.findingTypeId)
      ?.findingTypeTemplate?.properties.map(x => x.name) ?? []
  );
}

export const updateAnnotationShape = (): AppThunk => async (dispatch, getState) => {
  try {
    const { selectedAnnotationForEdit } = getState().annotationForm;
    const { findingLocalizationDto, sequencePath, selectedName, annotationStandardDetails } = getState().annotation;

    if (!annotationStandardDetails) {
      return;
    }
    if (!selectedAnnotationForEdit) {
      return;
    }

    const allProps = getFindingTypePropNames(annotationStandardDetails, selectedAnnotationForEdit);

    if (!allProps.length) {
      return;
    }

    const properties = selectedAnnotationForEdit.properties.map(x => ({
      name: x.name,
      value: x.value
    }));

    if (findingLocalizationDto && findingLocalizationDto.imageFileName) {
      allProps
        .filter(x => LOCALIZATION_FIELDS.includes(x))
        .forEach(propName => {
          const property = properties.find(x => x.name === propName);

          if (!property) {
            const newProperty = { name: propName, value: '' };
            localizationPropertyAssignment(newProperty, findingLocalizationDto);
            properties.push(newProperty);
            return;
          }

          localizationPropertyAssignment(property, findingLocalizationDto);
        });
    }

    const { id, shape, imageScale } = selectedAnnotationForEdit;

    const updatedAnnotation = {
      sequencePathId: sequencePath,
      imageFileName: selectedName,
      id,
      shape,
      imageScale,
      properties
    } as UpdateAnnotationShape;

    const url = 'annotations/updateshape';
    const response = await axios.put<boolean>(url, updatedAnnotation);

    dispatch(clearDirtyFlagOnFinding(id));
    dispatch(getSequenceAnnotations(true, true));
    dispatch(setSelectedAnnotationForEdit(null));
    dispatch(setAnnotationSetForUpdate(false));
    dispatch(setSaveFinding(false));
    ToastAnt('Updated');
  } catch (error) {
    ToastAnt('An error occured.');
  }
};

export const deleteAnnotation =
  (ids: string[]): AppThunk =>
  async (dispatch, getState) => {
    let successful = true;

    const { annotationsGrid } = getState().annotation;
    if (!annotationsGrid) {
      ToastAnt('An error occured.');
      return;
    }

    for (const id of ids) {
      try {
        const annotationForDelete = annotationsGrid.find(x => x.id === id);

        if (!annotationForDelete) {
          ToastAnt('An error occured.');
          return;
        }

        const { sequencePath, imageName } = annotationForDelete;

        const url = `annotations/${sequencePath}/${imageName}/${id}`;
        const response = await axios.delete(url);
      } catch (error) {
        successful = false;
        ToastAnt('An error occured.');
        break;
      }
    }

    if (successful) {
      ToastAnt('Deleted');
    }

    dispatch(getSequenceAnnotations(true, true));
    dispatch(setAnnotationIdsForDelete(null));
    dispatch(setGoFromFindingToImage(true));
  };

export const getFindingTypeProperties =
  (id: string, templateId: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setSelectedProperties([]));
    dispatch(setSelectedFindingTypeId(id));
    const response = await axios.get(`FindingTypeTemplates/${templateId}`);
    const properties = response.data.properties;
    if (properties) {
      dispatch(setSelectedProperties(properties));
    }
  };

export const sendFindingForAssessment =
  (description: string, assignee: AssessmentAssigneeOption): AppThunk =>
  async (dispatch, getState) => {
    const { sequencePath, selectedName: imageFileName, selectedSequenceInfo } = getState().annotation;
    const { id: annotationId, imageName: imageNameOnAnnotation } = getState().annotationForm.selectedAnnotationForEdit!;
    const { site, turbineName: turbine, inspection, bladePosition: blade, bladeSide: side } = selectedSequenceInfo!;

    dispatch(setSubmittingForm(true));

    const model: SendForAssessmentDto = {
      annotationId,
      site,
      turbine,
      inspection,
      sequencePath,
      imageFileName: !isStringNullOrEmpty(imageFileName) ? imageFileName : imageNameOnAnnotation,
      description,
      assignee,
      blade,
      side
    };

    try {
      const response = await axios.post(`annotations/sendforassessment`, model);
      dispatch(getAnnotationComments(false));
      dispatch(getSequenceAnnotations(true, true));
      dispatch(setSubmittingForm(false));
      dispatch(canCloseAssessment());
      ToastAnt('Assessment Sent');
    } catch (error) {
      console.log(error);
    }
  };

export const canCloseAssessment = (): AppThunk => async dispatch => {
  dispatch(setGoFromFindingToImage(true));
  dispatch(setCanSaveAssessment(false));
  dispatch(setShowCreateAnnotationModal(false));
};

export const getAnnotationWarrantyCaseStatus = (): AppThunk => async (dispatch, getState) => {
  const { selectedAnnotationForEdit } = getState().annotationForm;

  if (!selectedAnnotationForEdit) {
    dispatch(setWarrantyCaseClosed(false));
    return;
  } else {
    const response = await axios.get<boolean>(`annotations/get-warranty-case-status-for-finding/${selectedAnnotationForEdit.id}`);
    dispatch(setWarrantyCaseClosed(response.data));
  }
};

export const getAnnotationAssessment = (): AppThunk => async (dispatch, getState) => {
  const { selectedAnnotationForEdit } = getState().annotationForm;
  if (!selectedAnnotationForEdit) {
    dispatch(setLoadingAssessment(false));
    return;
  }

  try {
    dispatch(setLoadingAssessment(true));
    const response = await axios.get<AnnotationAssessmentInfo>(`annotations/getannotationassessment/${selectedAnnotationForEdit.id}`);
    dispatch(setAssessmentInfo(response.data));
  } catch (error) {
    console.log(error);
  }

  dispatch(setLoadingAssessment(false));
};
